import { Group, ScrollArea, Tabs, TabsProps } from '@mantine/core';
import { filter, isEmpty } from 'lodash/fp';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';

import { setRoute } from '@portals/redux/actions/routing';
import {
  CommonFeatureFlagsType,
  CommonPortalCapabilities,
  TabType,
} from '@portals/types';

import {
  useCommonFeatureFlags,
  useCommonPortalCapabilities,
} from '../../hooks/portal-config';
import { Page404 } from '../../pages/auth/Page404';
import {
  CanEditFn,
  CanViewFn,
  usePermissionAccess,
} from '../permission-access/use-permission-access';

interface LocationTabsProps<
  FeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  PortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities,
  TabContentComponentProps extends object = Record<string, unknown>,
  PathParams extends object = Record<string, string>,
  VisibleFnExtraParams extends object = Record<string, unknown>
> {
  tabs: TabType<
    FeatureFlags,
    PortalCapabilities,
    TabContentComponentProps,
    VisibleFnExtraParams
  >[];
  tabContentComponentProps?: TabContentComponentProps;
  pathParams?: PathParams;
  visibleFnExtraParams?: VisibleFnExtraParams;
}

interface GetAvailableTabsParams<
  FeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  PortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities
> {
  featureFlags: FeatureFlags;
  portalCapabilities: PortalCapabilities;
  canView: CanViewFn;
  canEdit: CanEditFn;
  isAdmin: boolean;
}

function getAvailableTabs<
  FeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  PortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities,
  TabContentComponentProps extends object = Record<string, unknown>,
  VisibleFnExtraParams extends object = Record<string, unknown>
>(
  tabs: TabType<
    FeatureFlags,
    PortalCapabilities,
    TabContentComponentProps,
    VisibleFnExtraParams
  >[],
  params: GetAvailableTabsParams<FeatureFlags, PortalCapabilities>,
  visibleFnExtraParams: VisibleFnExtraParams
) {
  return filter(
    ({ visible }) =>
      visible ? visible?.({ ...params, ...visibleFnExtraParams }) : true,
    tabs
  );
}

function findTab<
  FeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  PortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities,
  TabContentComponentProps extends object = Record<string, unknown>,
  VisibleFnExtraParams extends object = Record<string, unknown>
>(
  tabs: TabType<
    FeatureFlags,
    PortalCapabilities,
    TabContentComponentProps,
    VisibleFnExtraParams
  >[],
  tabName: string | undefined
) {
  if (!tabName) {
    return tabs[0];
  }

  return tabs.find((tab) => tab.name === tabName) || tabs[0];
}

export function LocationTabs<
  FeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  PortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities,
  TabContentComponentProps extends object = Record<string, unknown>,
  PathParams extends object = Record<string, string>,
  VisibleFnExtraParams extends object = Record<string, unknown>
>({
  tabs,
  tabContentComponentProps,
  pathParams,
  visibleFnExtraParams,
}: LocationTabsProps<
  FeatureFlags,
  PortalCapabilities,
  TabContentComponentProps,
  PathParams,
  VisibleFnExtraParams
>) {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const { tab: urlTabParam } = useParams<{ tab?: string } & PathParams>();
  const { canView, canEdit, isAdmin } = usePermissionAccess();
  const featureFlags = useCommonFeatureFlags() as FeatureFlags;
  const portalCapabilities =
    useCommonPortalCapabilities() as PortalCapabilities;

  const availableTabs = getAvailableTabs<
    FeatureFlags,
    PortalCapabilities,
    TabContentComponentProps,
    VisibleFnExtraParams
  >(
    tabs,
    {
      featureFlags,
      portalCapabilities,
      canView,
      canEdit,
      isAdmin,
    },
    visibleFnExtraParams
  );

  const selected = findTab<
    FeatureFlags,
    PortalCapabilities,
    TabContentComponentProps,
    VisibleFnExtraParams
  >(availableTabs, urlTabParam);

  useEffect(
    function navigateToFirstAvailableTabRoute() {
      // no available tab found
      if (!selected) {
        return;
      }

      // there's no `tab` param in the url
      if (!urlTabParam) {
        const computedPath = generatePath(match.path, { ...pathParams });
        dispatch(setRoute(`${computedPath}/${selected.name}`, true));

        return;
      }

      if (urlTabParam !== selected.name) {
        const computedPath = generatePath(match.path, {
          tab: selected.name,
          ...pathParams,
        });
        dispatch(setRoute(computedPath, true));
      }
    },
    [dispatch, match.path, pathParams, selected, urlTabParam]
  );

  if (!selected) {
    return null;
  }

  if (!selected || isEmpty(availableTabs) || selected.disabled) {
    return <Page404 />;
  }

  const onTabChange = (tabName: string) => {
    const tab = findTab(availableTabs, tabName);
    const computedPath = generatePath(match.path, {
      tab: tab.name,
      ...pathParams,
    });

    dispatch(setRoute(computedPath));
  };

  return (
    <Tabs
      keepMounted={false}
      value={selected.name}
      onTabChange={onTabChange}
      styles={tabsStyles}
    >
      <ScrollArea type="auto">
        <Tabs.List>
          {availableTabs.map(({ id, name, title, badge, disabled }) => (
            <Group key={id} spacing={0} className="location-tab-wrapper">
              <Tabs.Tab
                value={name}
                rightSection={badge}
                disabled={disabled}
                data-testid={'location-tab-' + id}
              >
                {title}
              </Tabs.Tab>

              {badge ? (
                <Group h="100%" className="location-tab-badge-wrapper">
                  {badge}
                </Group>
              ) : null}
            </Group>
          ))}
        </Tabs.List>
      </ScrollArea>

      {availableTabs.map(({ id, name, Component }) => (
        <Tabs.Panel key={id} value={name}>
          <Component {...tabContentComponentProps} />
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}

const tabsStyles: TabsProps['styles'] = (theme) => ({
  root: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    height: '100%',
  },
  tabsList: {
    flexWrap: 'nowrap',
    width: 'fit-content',
    borderWidth: 3,

    '.location-tab-wrapper': {
      position: 'relative',

      '.location-tab-badge-wrapper': {
        zIndex: 1,
        position: 'absolute',
        paddingRight: theme.spacing.xs,
        bottom: 0,
        right: 0,
      },
    },
  },
  tab: {
    marginBottom: -3,
    borderWidth: 3,
  },
  panel: {
    paddingBlock: theme.spacing.md,
  },

  // This is a hacky way to display a badge over a disabled tab. The original
  // badge is rendered and takes up space, but is hidden with opacity.
  // An additional badge is then rendered alongside the tab, which is positioned
  // absolutely over the original badge.
  tabRightSection: {
    opacity: 0,
    pointerEvents: 'none',
    marginRight: theme.spacing.xs,
  },
});
